/**
 * Invoice History Component
 * Displays patient's invoice history
 */

import { FileText, Calendar, DollarSign } from 'lucide-react';
import type { DentalOfficeInvoice } from '../../types';
import { formatDate, formatCurrency } from '../../lib/utils';
import { LoadingSpinner } from '../LoadingSpinner';

interface InvoiceHistoryProps {
  invoices: DentalOfficeInvoice[] | undefined;
  isLoading: boolean;
}

export function InvoiceHistory({ invoices, isLoading }: InvoiceHistoryProps) {
  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner text="Hleð reikningum..." />
      </div>
    );
  }

  if (!invoices || invoices.length === 0) {
    return (
      <div className="text-center py-12">
        <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">Engir reikningar skráðir</p>
      </div>
    );
  }

  const totalOutstanding = invoices
    .filter((inv) => inv.status === 'pending' || inv.status === 'overdue')
    .reduce((sum, inv) => sum + inv.total, 0);

  return (
    <div className="space-y-6">
      {/* Summary */}
      {totalOutstanding > 0 && (
        <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Útistandandi</span>
            <span className="text-lg font-bold text-warning">
              {formatCurrency(totalOutstanding)}
            </span>
          </div>
        </div>
      )}

      {/* Invoice List */}
      <div className="space-y-4">
        {invoices.map((invoice) => (
          <div
            key={invoice.id}
            className="bg-white border border-gray-200 rounded-lg p-4 hover:border-brand-primary transition-colors"
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <h4 className="font-semibold text-gray-900">
                  Reikningur #{invoice.invoice_number}
                </h4>
                {invoice.notes && (
                  <p className="text-sm text-gray-600 mt-1">{invoice.notes}</p>
                )}
              </div>
              <span
                className={`px-3 py-1 rounded-full text-xs font-medium ${
                  invoice.status === 'paid'
                    ? 'bg-success/10 text-success'
                    : invoice.status === 'pending'
                    ? 'bg-warning/10 text-warning'
                    : invoice.status === 'overdue'
                    ? 'bg-error/10 text-error'
                    : 'bg-gray-100 text-gray-800'
                }`}
              >
                {invoice.status === 'paid'
                  ? 'Greitt'
                  : invoice.status === 'pending'
                  ? 'Ógreitt'
                  : invoice.status === 'overdue'
                  ? 'Tímabært'
                  : 'Hætt við'}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm text-gray-600 mb-3">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>Útgáfudagur: {formatDate(invoice.invoice_date)}</span>
              </div>

              {invoice.due_date && (
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <span>Gjalddagi: {formatDate(invoice.due_date)}</span>
                </div>
              )}
            </div>

            {invoice.payment_date && (
              <div className="text-sm text-gray-600 mb-3">
                <span className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  Greitt: {formatDate(invoice.payment_date)}
                </span>
              </div>
            )}

            <div className="pt-3 border-t flex justify-between items-center">
              <span className="text-sm text-gray-600">Heildarupphæð</span>
              <span className="text-lg font-bold text-gray-900">
                {formatCurrency(invoice.total)}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
